import { useEffect, useRef, useState } from "react";
import { PaperPlaneTilt, CircleNotch } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const lastRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/chat");
    setMessages(data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (messages && messages.length && lastRef.current !== messages[messages.length - 1].id) {
      lastRef.current = messages[messages.length - 1].id;
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post("/chat", { text: text.trim() });
      setText("");
      await load();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="animate-fade-up flex flex-col" style={{ height: "calc(100vh - 220px)" }}>
      <h1 className="font-heading font-black text-4xl uppercase tracking-tighter mb-2">Group Chat</h1>
      <div className="flex-1 overflow-y-auto border border-border bg-card rounded-sm p-4 space-y-4" data-testid="chat-messages">
        {messages === null ? (
          <div className="flex justify-center py-10"><CircleNotch size={28} className="animate-spin text-primary" /></div>
        ) : messages.length === 0 ? (
          <p className="text-center text-muted-foreground py-10">No messages yet. Say hello 👋</p>
        ) : (
          messages.map((m) => {
            const mine = m.user_id === user.id;
            return (
              <div key={m.id} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-message-${m.id}`}>
                <Avatar name={m.nickname || m.name} path={m.avatar_path} size={34} enlargeable />
                <div className={`max-w-[75%] ${mine ? "items-end text-right" : ""} flex flex-col`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-bold text-muted-foreground">{m.nickname || m.name}</span>
                    {(m.role === "admin" || m.role === "primary_admin") && <span className="text-[9px] font-bold uppercase tracking-widest text-primary bg-primary/15 px-1.5 py-0.5 rounded-sm">Admin</span>}
                  </div>
                  <div className={`px-3.5 py-2 rounded-md border text-[15px] leading-snug break-words ${mine ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>
                    {m.text}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 mt-3">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          data-testid="chat-input"
          className="flex-1 bg-card border border-border rounded-sm px-4 h-12 text-base focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button type="submit" disabled={sending} data-testid="chat-send" className="w-12 h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm flex items-center justify-center transition-colors">
          <PaperPlaneTilt size={22} weight="fill" />
        </button>
      </form>
    </div>
  );
}
